import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { PrintRun, Book, Warehouse, PrintRunHistory } from '@/api/entities';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { History, Printer, Book as BookIcon, Package, DollarSign, User as UserIcon, ArrowRight } from 'lucide-react';

const typeLabels = {
    main_print: 'Основной тираж',
    reprint: 'Допечатка'
};

const statusLabels = {
    draft: 'Черновик',
    in_production: 'В печати',
    delivered: 'Доставлен'
};

export default function PrintRunDetails() {
    const location = useLocation();
    const [id, setId] = useState(null);
    const [printRun, setPrintRun] = useState(null);
    const [book, setBook] = useState(null);
    const [warehouse, setWarehouse] = useState(null);
    const [history, setHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const loadData = async (printRunId) => {
        if (!printRunId) {
            setIsLoading(false);
            setPrintRun(null);
            return;
        }
        setIsLoading(true);
        try {
            const [allRuns, allBooks, allWarehouses] = await Promise.all([
                PrintRun.list(),
                Book.list(),
                Warehouse.list({ is_active: true })
            ]);

            const runData = allRuns.find(run => run.id === printRunId);
            
            if (!runData) {
                console.error('Тираж с ID не найден:', printRunId);
                setPrintRun(null);
                return;
            }
            
            setPrintRun(runData);

            const historyData = await PrintRunHistory.filter({ print_run_id: printRunId }, '-created_date');
            
            setHistory(historyData || []);
            setBook((allBooks || []).find(b => b.id === runData.book_id) || null);
            setWarehouse((allWarehouses || []).find(w => w.id === runData.warehouse_id) || null);
    
        } catch (error) {
            console.error("Не удалось загрузить детали тиража:", error);
            setPrintRun(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const urlId = searchParams.get('id');
        setId(urlId);
    }, [location.search]);

    useEffect(() => {
        if (id) {
            loadData(id);
        }
    }, [id]);

    if (isLoading) {
        return <div className="p-6 text-center">Загрузка информации о тираже...</div>;
    }

    if (!printRun) {
        return <div className="p-6 text-center font-bold text-red-600">Тираж не найден.</div>;
    }
    
    return (
        <div className="min-h-screen gradient-bg p-6">
            <div className="max-w-5xl mx-auto space-y-6">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Тираж #{printRun.document_number}</h1>
                    <p className="text-slate-600">от {format(new Date(printRun.document_date), 'dd MMMM yyyy', { locale: ru })}</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-6">
                        <Card className="elegant-card">
                            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Printer className="w-5 h-5" />Основная информация</CardTitle></CardHeader>
                            <CardContent className="grid grid-cols-2 gap-4 pt-4">
                                <div><p className="text-sm text-slate-500">Книга</p><p className="font-medium">{book?.title}</p></div>
                                <div><p className="text-sm text-slate-500">Тип</p><p className="font-medium">{typeLabels[printRun.type]}</p></div>
                                <div><p className="text-sm text-slate-500">ISBN</p><p className="font-medium">{printRun.isbn || 'Наследуется'}</p></div>
                            </CardContent>
                        </Card>
                        <Card className="elegant-card">
                             <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Package className="w-5 h-5" />Производство и доставка</CardTitle></CardHeader>
                             <CardContent className="grid grid-cols-2 gap-4 pt-4">
                                <div><p className="text-sm text-slate-500">Количество</p><p className="font-medium">{printRun.quantity?.toLocaleString()} экз.</p></div>
                                <div><p className="text-sm text-slate-500">Склад</p><p className="font-medium">{warehouse?.name}</p></div>
                                <div><p className="text-sm text-slate-500">Ожидаемая дата</p><p className="font-medium">{printRun.expected_delivery_date ? format(new Date(printRun.expected_delivery_date), 'dd.MM.yyyy') : 'Не указана'}</p></div>
                                <div><p className="text-sm text-slate-500">Примечания</p><p className="font-medium">{printRun.notes || 'Нет'}</p></div>
                             </CardContent>
                        </Card>
                    </div>
                    <div className="space-y-6">
                        <Card className="elegant-card">
                            <CardHeader><CardTitle className="text-lg">Статус</CardTitle></CardHeader>
                            <CardContent className="pt-4"><Badge>{statusLabels[printRun.status]}</Badge></CardContent>
                        </Card>
                         <Card className="elegant-card">
                            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><DollarSign className="w-5 h-5" />Финансы</CardTitle></CardHeader>
                            <CardContent className="pt-4">
                                <div><p className="text-sm text-slate-500">Себестоимость</p><p className="font-medium">{(printRun.cost_per_unit || 0).toLocaleString()} ₽ / экз.</p></div>
                                <div className="mt-2"><p className="text-sm text-slate-500">Общая стоимость</p><p className="font-bold text-xl text-green-600">{((printRun.cost_per_unit || 0) * (printRun.quantity || 0)).toLocaleString()} ₽</p></div>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                <Card className="elegant-card">
                    <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><History className="w-5 h-5" />История тиража</CardTitle></CardHeader>
                    <CardContent className="space-y-4 pt-4">
                        {history.length > 0 ? history.map(h => (
                            <div key={h.id} className="flex gap-4 items-start">
                                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><UserIcon className="w-5 h-5 text-slate-500" /></div>
                                <div className="flex-grow">
                                    <p className="font-medium text-slate-800">{h.details}</p>
                                    {h.action === 'updated' && h.old_value && h.new_value && 
                                        <p className="text-sm text-slate-500 flex items-center gap-1.5"><span className="line-through">{h.old_value}</span><ArrowRight className="w-3 h-3" /><span>{h.new_value}</span></p>
                                    }
                                    <p className="text-xs text-slate-400 mt-1">{h.user_name} • {format(new Date(h.created_date), 'dd MMM yyyy, HH:mm', { locale: ru })}</p>
                                </div>
                            </div>
                        )) : (
                            <p className="text-slate-500 text-center py-4">История изменений для этого тиража пуста.</p>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}